import React, { useEffect } from 'react';
import { styled } from '@compiled/react';
import groupBy from 'lodash/groupBy';
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { categoryFilter_softwareReleasesVersionRelatedWork$key } from '@atlassian/jira-relay/src/__generated__/categoryFilter_softwareReleasesVersionRelatedWork.graphql';
import { Lozenge } from './lozenge/index.tsx';
import messages from './messages.tsx';
import { MoreLozengeAsync as MoreLozenge } from './more-lozenge/async.tsx';

export const RELATED_WORK_CATEGORY_MORE_THRESHOLD = 5;

type Props = {
	relatedWorkEdges: categoryFilter_softwareReleasesVersionRelatedWork$key;
	selectedCategory: string | undefined;
	onCategorySelected: (arg1: string | undefined) => void;
};

export const CategoryFilter = ({
	relatedWorkEdges,
	selectedCategory,
	onCategorySelected,
}: Props) => {
	const { formatMessage } = useIntl();

	const edges = useFragment(
		graphql`
			fragment categoryFilter_softwareReleasesVersionRelatedWork on JiraVersionRelatedWorkV2Edge
			@relay(plural: true) {
				node @required(action: NONE) {
					category @required(action: NONE)
				}
			}
		`,
		relatedWorkEdges,
	);

	const edgesByCategory = groupBy(edges, (edge) => edge?.node.category);

	const sortedCategoriesWithCounts = Object.keys(edgesByCategory)
		.map((category): [string, number] => [category, edgesByCategory[category].length])
		.sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB));

	const isknownCategory =
		selectedCategory === undefined ||
		sortedCategoriesWithCounts.some(([category]) => category === selectedCategory);

	// If the selected category is unknown, un-select it (the selected category comes from
	// a query param, so we can't trust it will always be valid).
	useEffect(() => {
		if (!isknownCategory) {
			onCategorySelected(undefined);
		}
	}, [isknownCategory, onCategorySelected]);

	const categoriesWithCountWithinThreshold = sortedCategoriesWithCounts.slice(
		0,
		RELATED_WORK_CATEGORY_MORE_THRESHOLD,
	);
	const categoriesWithCountAfterThreshold = sortedCategoriesWithCounts.slice(
		RELATED_WORK_CATEGORY_MORE_THRESHOLD,
	);

	return (
		<Container>
			<Lozenge
				label={formatMessage(messages.all)}
				count={edges.length ?? 0}
				isSelected={selectedCategory === undefined}
				onClick={() => onCategorySelected(undefined)}
			/>
			{/* eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-explicit-any */}
			{categoriesWithCountWithinThreshold.map(([category, count]: [any, any]) => (
				<Lozenge
					key={category}
					label={category}
					count={count}
					isSelected={category === selectedCategory}
					onClick={() => onCategorySelected(category)}
				/>
			))}
			{categoriesWithCountAfterThreshold.length > 0 && (
				<MoreLozenge
					categories={categoriesWithCountAfterThreshold.map(
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						([category, count]: [any, any]) => ({
							label: category,
							count,
							isSelected: category === selectedCategory,
							onClick: () => onCategorySelected(category),
						}),
					)}
				/>
			)}
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.100'),
});
